/**
 * Pure helpers for the TechDocs version picker + default-landing redirect
 * (Story 9.3) and the tier dimension layered on top (Story 6.3). Kept free of
 * React/Backstage imports so the four-case disambiguation rule (AC 7-10), the
 * combined tier redirect rule, and the sub-path-preserving switches are
 * unit-testable without a browser.
 */
import { ResolvedTierConfig } from './tiers/types';

/** The `latest` alias segment used in reader URLs and the manifest pointer. */
export const LATEST = 'latest';

/**
 * Normalized view of a `versions.json` manifest.
 *
 * `versions` holds the real version tags (e.g. `v1.0.0`) in manifest order;
 * `latest` is the pointer tag (or `null` for an unversioned entity).
 */
export type VersionsManifest = {
  versions: string[];
  latest: string | null;
};

/** Outcome of the redirect disambiguation rule for a given reader path. */
export type RedirectDecision =
  | { redirect: true; to: string }
  | { redirect: false };

/**
 * Parse a raw `versions.json` string (the shape returned by
 * `techdocsStorageApi.getEntityDocs(entityRef, 'versions.json')`).
 *
 * Returns `null` when the payload is not valid JSON or lacks a `versions`
 * array, so callers can fail closed (no picker, no redirect) per AC 10.
 */
export function parseVersionsManifest(raw: string): VersionsManifest | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (
    typeof parsed !== 'object' ||
    parsed === null ||
    !Array.isArray((parsed as { versions?: unknown }).versions)
  ) {
    return null;
  }

  const entries = (parsed as { versions: unknown[] }).versions;
  const versions = entries
    .map(entry =>
      typeof entry === 'object' && entry !== null
        ? (entry as { version?: unknown }).version
        : undefined,
    )
    .filter((v): v is string => typeof v === 'string' && v.length > 0);

  const latestRaw = (parsed as { latest?: unknown }).latest;
  const latest =
    typeof latestRaw === 'string' && latestRaw.length > 0 ? latestRaw : null;

  return { versions, latest };
}

/**
 * The ordered list of versions the picker offers: the `latest` alias first,
 * then each real version tag. A manifest with no real tags yields only
 * `['latest']`, which the picker treats as "nothing to switch to" (AC 5).
 */
export function selectableVersions(manifest: VersionsManifest): string[] {
  return [LATEST, ...manifest.versions];
}

/** Strip leading slashes so `/foo/` and `foo/` are treated identically. */
function stripLeadingSlash(rest: string): string {
  return rest.replace(/^\/+/, '');
}

/** First path segment of a reader sub-path, or `''` when there is none. */
function firstSegment(rest: string): string {
  return stripLeadingSlash(rest).split('/')[0] ?? '';
}

/**
 * Decide whether a reader path under `/docs/<ns>/<kind>/<name>/` needs a
 * canonicalizing redirect. `rest` is everything after that entity prefix
 * (the route splat); `versions` is the real version-tag list from the
 * manifest. Disambiguation is by manifest membership, never by `v*.*.*`
 * shape (AC 9).
 *
 *   1. empty rest                      -> redirect to `latest/`
 *   2. first segment === `latest`      -> no redirect
 *   3. first segment in version list   -> no redirect
 *   4. otherwise (page path, no ver)   -> redirect to `latest/<rest>`
 */
export function decideRedirect(
  rest: string,
  versions: string[],
): RedirectDecision {
  const normalized = stripLeadingSlash(rest);
  const segment = firstSegment(normalized);

  if (segment === '') {
    return { redirect: true, to: `${LATEST}/` };
  }
  if (segment === LATEST || versions.includes(segment)) {
    return { redirect: false };
  }
  return { redirect: true, to: `${LATEST}/${normalized}` };
}

/** Index of the version segment in a split pathname (at or after `start`), or -1 if none. */
function versionSegmentIndex(
  segments: string[],
  versions: string[],
  start = 0,
): number {
  const idx = segments
    .slice(start)
    .findIndex(s => s === LATEST || versions.includes(s));
  return idx >= 0 ? idx + start : -1;
}

/**
 * The currently selected version for a reader pathname: the first path
 * segment that is `latest` or a known manifest tag, defaulting to `latest`
 * when the path carries no explicit version segment. Works in both reader
 * contexts (standalone `/docs/...` and the entity-page `.../docs/...` tab)
 * because it keys off manifest membership, not a fixed prefix.
 */
export function currentVersionFromPath(
  pathname: string,
  versions: string[],
): string {
  const segments = pathname.split('/');
  const idx = versionSegmentIndex(segments, versions);
  return idx >= 0 ? segments[idx] : LATEST;
}

/** Everything the picker needs to navigate a version switch. */
export type VersionSwitch = {
  /** Absolute pathname to navigate to, page sub-path preserved (AC 4). */
  path: string;
  /** Absolute pathname of the target version's root index (AC 4 fallback). */
  rootPath: string;
  /** Storage-relative doc path for the preserved sub-path, for existence probing. */
  docPath: string;
  /** True when there is a page sub-path beyond the version root. */
  hasSubPath: boolean;
};

/**
 * Compute the navigation target for switching to `targetVersion`, preserving
 * the page sub-path within the docs site (AC 4). When the pathname carries an
 * explicit version segment (always true on the standalone route once the
 * redirect wrapper has canonicalized it), that segment is swapped in place so
 * the surrounding prefix — `/docs/...` or the entity-page `.../docs/...` tab —
 * is preserved untouched. When no version segment is present (e.g. the
 * entity-tab root at implied `latest`), the version is appended at the reader
 * root.
 *
 * Story 6.3: pass `anchor` (the reader page's entity ref) so the version and
 * tier scans start after the entity prefix — without it, an entity *named*
 * after a tier id (or `latest`) would satisfy the membership checks.
 */
export function computeVersionSwitch(
  pathname: string,
  versions: string[],
  targetVersion: string,
  tierIds: string[] = [],
  anchor?: ReaderEntityAnchor,
): VersionSwitch {
  const segments = pathname.split('/');
  const root = anchor ? readerRootIndex(segments, anchor) : -1;
  const idx = versionSegmentIndex(segments, versions, root >= 0 ? root : 0);

  if (idx >= 0) {
    const swapped = [...segments];
    swapped[idx] = targetVersion;
    // Drop the empty trailing segment from a `.../` URL so the doc path is
    // always built with an explicit trailing slash — otherwise a sub-path on a
    // URL with no trailing slash (`.../latest/architecture`) would probe the
    // malformed `<version>/architectureindex.html` and spuriously 404.
    const subSegments = segments.slice(idx + 1).filter(Boolean);
    const hasSubPath = subSegments.length > 0;
    const subPath = hasSubPath ? `${subSegments.join('/')}/` : '';
    // Story 6.3 (AC 6): under the tier layout the storage-relative doc path is
    // `<tier>/<version>/…`, so the existence probe must carry the tier prefix
    // when the version segment sits inside a tier subtree. With an anchor,
    // the tier can only be the segment at the reader root.
    const tier =
      idx > 0 &&
      tierIds.includes(segments[idx - 1]) &&
      (root < 0 || idx - 1 === root)
        ? segments[idx - 1]
        : null;
    return {
      path: swapped.join('/'),
      rootPath: `${swapped.slice(0, idx + 1).join('/')}/`,
      docPath: `${tier ? `${tier}/` : ''}${targetVersion}/${subPath}`,
      hasSubPath,
    };
  }

  const base = pathname.endsWith('/') ? pathname : `${pathname}/`;
  const path = `${base}${targetVersion}/`;
  const trimmed = [...segments];
  while (trimmed.length > 0 && trimmed[trimmed.length - 1] === '') {
    trimmed.pop();
  }
  const lastIdx = trimmed.length - 1;
  const lastSegment = lastIdx >= 0 ? trimmed[lastIdx] : '';
  const tier =
    tierIds.includes(lastSegment) && (root < 0 || lastIdx >= root)
      ? lastSegment
      : null;
  return {
    path,
    rootPath: path,
    docPath: `${tier ? `${tier}/` : ''}${targetVersion}/`,
    hasSubPath: false,
  };
}

// --- Story 6.3: tier dimension ---------------------------------------------

/**
 * The entity triple reader URLs carry in both contexts
 * (`/docs/<ns>/<kind>/<name>/…` and `/catalog/<ns>/<kind>/<name>/docs/…`).
 * Structurally compatible with `CompoundEntityRef` so hooks can pass the
 * reader page's `entityRef` straight through.
 */
export type ReaderEntityAnchor = {
  namespace: string;
  kind: string;
  name: string;
};

/**
 * Index of the first pathname segment after the entity prefix (the entity
 * triple plus the literal `docs` mount of the entity-page tab) — the only
 * position a tier segment can legally occupy. Anchoring membership scans here
 * keeps an entity *named* after a tier id (`customer`, `public`, …) — or a
 * kind/namespace that collides with one — from being read as a tier segment.
 * Returns -1 when the triple is absent (not a reader URL).
 */
function readerRootIndex(
  segments: string[],
  anchor: ReaderEntityAnchor,
): number {
  const ns = anchor.namespace.toLowerCase();
  const kind = anchor.kind.toLowerCase();
  const name = anchor.name.toLowerCase();
  for (let i = 0; i + 2 < segments.length; i++) {
    if (
      segments[i].toLowerCase() === ns &&
      segments[i + 1].toLowerCase() === kind &&
      segments[i + 2].toLowerCase() === name
    ) {
      const next = i + 3;
      return segments[next] === 'docs' ? next + 1 : next;
    }
  }
  return -1;
}

/** A reader splat split into its leading tier segment and the remainder. */
export type TierPathSplit = {
  /** The leading segment when it is a resolved tier id, else null. */
  tier: string | null;
  /** Everything after the tier segment (or the input, when no tier leads). */
  rest: string;
};

/**
 * Split a reader sub-path into its leading tier segment and the remainder.
 * Membership is exact against `tierIds` (the entity's resolved tier list) —
 * never shape-matching, consistent with the version rule (Epic 9 AC 9).
 */
export function stripTierSegment(
  rest: string,
  tierIds: string[],
): TierPathSplit {
  const normalized = stripLeadingSlash(rest);
  const segment = firstSegment(normalized);
  if (segment !== '' && tierIds.includes(segment)) {
    return { tier: segment, rest: normalized.slice(segment.length + 1) };
  }
  return { tier: null, rest: normalized };
}

/**
 * The tier a reader pathname is currently inside: the segment directly after
 * the entity prefix when it is a resolved tier id, or null when the path
 * carries no tier segment (legacy pre-tier URL, entity-page tab root, or
 * un-tiered entity). Anchoring on the entity triple — rather than scanning
 * every segment — keeps an entity named after a tier id from reading as a
 * tier segment. Tier ids can never collide with the version namespace
 * (`latest` and `v*.*.*` are reserved by the parser). Falls back to a
 * whole-path membership scan when the triple is absent from the pathname.
 */
export function currentTierFromPath(
  pathname: string,
  tierIds: string[],
  anchor: ReaderEntityAnchor,
): string | null {
  if (tierIds.length === 0) {
    return null;
  }
  const segments = pathname.split('/');
  const root = readerRootIndex(segments, anchor);
  if (root >= 0) {
    const candidate = segments[root] ?? '';
    return tierIds.includes(candidate) ? candidate : null;
  }
  return segments.find(s => tierIds.includes(s)) ?? null;
}

/**
 * The combined tier × version redirect rule from the versioning ADR
 * ("Combined redirect disambiguation"). `rest` is the route splat after the
 * entity prefix; `config` is the entity's resolved tier configuration (null
 * when the annotation failed to parse or the catalog lookup failed — fail
 * closed to Epic 9 behavior); `manifests` are the already-fetched per-scope
 * `versions.json` manifests (null where absent in blob).
 *
 * Rule, in order:
 *   2. seg1 ∈ tier ids        → Epic 9 four-case rule within that tier —
 *                               *regardless of migration state*. A validated
 *                               tier segment scopes the URL to that tier even
 *                               when the blob hasn't republished yet; staying
 *                               put (a truthful, diagnosable URL) beats
 *                               bouncing to a root-scope page path that 404s
 *                               just the same. This is also what keeps the
 *                               reader's `<tier>/latest/` fast path (which
 *                               renders before any manifest resolves) exactly
 *                               consistent with this rule.
 *
 * Then, unmigrated (no default-tier manifest): Epic 9's rule against the
 * entity root, including its no-manifest no-op (AC 5 — the passive-migration
 * guarantee). Otherwise, migrated:
 *   1. empty rest             → `<defaultTier>/latest/`
 *   3. seg1 == latest or ∈ default tier's versions (legacy pre-tier bookmark)
 *                             → `<defaultTier>/<rest>`
 *   4. otherwise (page path)  → `<defaultTier>/latest/<rest>`
 *
 * Fail-closed (null config): Epic 9's rule against the entity root.
 */
export function decideTieredRedirect(
  rest: string,
  config: ResolvedTierConfig | null,
  manifests: {
    /** The default tier's manifest; null ⇒ not migrated to the tier layout. */
    defaultTier: VersionsManifest | null;
    /** Manifest of the tier named by the first segment, when it is one. */
    seg1Tier: VersionsManifest | null;
    /** Entity-root manifest, for the pre-tier fallback. */
    root: VersionsManifest | null;
  },
): RedirectDecision {
  if (!config) {
    return manifests.root
      ? decideRedirect(rest, manifests.root.versions)
      : { redirect: false };
  }

  const tierIds = config.tiers.map(t => t.id);
  const normalized = stripLeadingSlash(rest);

  const { tier, rest: afterTier } = stripTierSegment(normalized, tierIds);
  if (tier) {
    const inner = decideRedirect(afterTier, manifests.seg1Tier?.versions ?? []);
    return inner.redirect
      ? { redirect: true, to: `${tier}/${inner.to}` }
      : { redirect: false };
  }

  if (!manifests.defaultTier) {
    return manifests.root
      ? decideRedirect(rest, manifests.root.versions)
      : { redirect: false };
  }

  if (normalized === '') {
    return { redirect: true, to: `${config.defaultTier}/${LATEST}/` };
  }

  const segment = firstSegment(normalized);
  if (segment === LATEST || manifests.defaultTier.versions.includes(segment)) {
    return { redirect: true, to: `${config.defaultTier}/${normalized}` };
  }
  return {
    redirect: true,
    to: `${config.defaultTier}/${LATEST}/${normalized}`,
  };
}

/**
 * Compute the navigation target for switching to `targetTier`, preserving the
 * page sub-path and the current version when the destination tier has it
 * (AC 3). `versions` locates the version segment in the current path (the
 * current tier's list); `targetVersions` decides whether the version survives
 * the switch (the destination tier's list — per-tier manifests diverge, ADR
 * T1/T2). Falls back to `latest` otherwise. The returned shape mirrors
 * `computeVersionSwitch` so callers share the probe-then-fallback pattern.
 *
 * `anchor` (the reader page's entity ref) pins the tier/version scans to the
 * segments after the entity prefix, so an entity named after a tier id can't
 * make the switch split at its own name.
 */
export function computeTierSwitch(
  pathname: string,
  tierIds: string[],
  versions: string[],
  targetTier: string,
  targetVersions: string[],
  anchor: ReaderEntityAnchor,
): VersionSwitch {
  const segments = pathname.split('/');
  const root = readerRootIndex(segments, anchor);
  // With an anchor match the tier can only sit at the reader root; the
  // whole-path scan survives only as a fallback for non-reader-shaped paths.
  let tierIdx: number;
  if (root >= 0) {
    tierIdx = tierIds.includes(segments[root] ?? '') ? root : -1;
  } else {
    tierIdx = segments.findIndex(s => tierIds.includes(s));
  }

  let prefix: string[];
  let after: string[];
  if (tierIdx >= 0) {
    prefix = segments.slice(0, tierIdx);
    after = segments.slice(tierIdx + 1);
  } else {
    const versionIdx = versionSegmentIndex(
      segments,
      versions,
      root >= 0 ? root : 0,
    );
    if (versionIdx >= 0) {
      // Legacy pre-tier path: the tier slots in before the version segment.
      prefix = segments.slice(0, versionIdx);
      after = segments.slice(versionIdx);
    } else {
      // Neither segment present (e.g. the entity-tab root at implied
      // default/latest): treat the whole path as the reader root, matching
      // computeVersionSwitch's append behavior.
      prefix = [...segments];
      while (prefix.length > 0 && prefix[prefix.length - 1] === '') {
        prefix.pop();
      }
      after = [];
    }
  }

  const hasVersionSegment =
    after.length > 0 && (after[0] === LATEST || versions.includes(after[0]));
  const currentVersion = hasVersionSegment ? after[0] : LATEST;
  const subSegments = (hasVersionSegment ? after.slice(1) : after).filter(
    Boolean,
  );
  const hasSubPath = subSegments.length > 0;
  const subPath = hasSubPath ? `${subSegments.join('/')}/` : '';

  const targetVersion =
    currentVersion !== LATEST && targetVersions.includes(currentVersion)
      ? currentVersion
      : LATEST;

  const rootPath = `${prefix.join('/')}/${targetTier}/${targetVersion}/`;
  return {
    path: `${rootPath}${subPath}`,
    rootPath,
    docPath: `${targetTier}/${targetVersion}/${subPath}`,
    hasSubPath,
  };
}
